import * as SelectPrimitive from "@radix-ui/react-select";
import { Plus, X } from "lucide-react";
import * as React from "react";

import { cn } from "../../../utils";
import { Button } from "../../Button";
import { Command, CommandInput, CommandList } from "../../Command";
import {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContentPrimitive,
  ResponsiveDialogOverlay,
  ResponsiveDialogPortal,
} from "../../modal";
import { PopoverContent } from "../../Popover";
import { SelectContext } from "./context";
import {
  SelectScrollDownButton,
  SelectScrollUpButton,
} from "./select-scroll-buttons";

const HiddenHydrationList = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute -left-[10000px] top-0 size-0 overflow-hidden opacity-0"
    >
      <Command shouldFilter={false}>{children}</Command>
    </div>
  );
};

const MobileSelectDrawer = ({
  open,
  onOpenChange,
  label,
  searchable,
  className,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label?: React.ReactNode;
  searchable: boolean;
  className?: string;
  children: React.ReactNode;
}) => {
  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogPortal>
        <ResponsiveDialogOverlay className="z-[500000] bg-black/40 backdrop-blur-none data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <ResponsiveDialogContentPrimitive
          className={cn(
            "fixed inset-x-0 bottom-0 z-[500001] flex flex-col overflow-hidden rounded-t-xl border-t bg-white text-gray-900",
            searchable ? "h-[70dvh] max-h-[70dvh]" : "max-h-[70dvh]",
            "shadow-[0_-4px_16px_rgba(0,0,0,0.1)]",
            "data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom data-[state=open]:duration-300",
            "data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=closed]:fade-out-0 data-[state=closed]:duration-200",
            className,
          )}
        >
          <div className="flex h-full min-h-0 flex-col">
            {label ? (
              <div className="px-4 pb-2 pt-4 text-base font-semibold text-gray-900">
                {label}
              </div>
            ) : null}
            <ResponsiveDialogClose asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-4 top-4"
                aria-label="Close"
                onPointerDown={(e) => e.preventDefault()}
              >
                <X className="size-4" />
              </Button>
            </ResponsiveDialogClose>
            <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
          </div>
        </ResponsiveDialogContentPrimitive>
      </ResponsiveDialogPortal>
    </ResponsiveDialog>
  );
};

type SelectContentProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Content
> & {
  label?: React.ReactNode;
};

export function SelectContent({
  className,
  children,
  position = "item-aligned",
  label: contentLabel,
  ...props
}: SelectContentProps) {
  const ctx = React.useContext(SelectContext);
  const {
    align = "start",
    side = "bottom",
    sideOffset = 4,
    ...enhancedContentProps
  } = props;

  // -------------------------------------------------------------------------
  // Simple Radix branch — item-aligned positioning
  // -------------------------------------------------------------------------
  if (ctx.isSimple) {
    return (
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          className={cn(
            "relative z-[500000] max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-white text-gray-900 shadow-md",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            position === "popper" &&
              "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
            className,
          )}
          position={position}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            className={cn(
              "p-1",
              position === "popper" &&
                "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
            )}
          >
            {children}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    );
  }

  // -------------------------------------------------------------------------
  // Enhanced branch — Popover + Command
  // -------------------------------------------------------------------------
  const {
    isMobile,
    open,
    handleOpenChange,
    searchable,
    creatable,
    search,
    setSearch,
    disabled,
    label: contextLabel,
    handleItemSelect,
    itemLabelsByValue,
  } = ctx;

  const sheetLabel = contextLabel ?? contentLabel;

  const trimmed = search.trim();
  const normalizedSearch = trimmed.toLowerCase();
  const exactMatchExists = [...itemLabelsByValue.current.entries()].some(
    ([value, label]) =>
      value.toLowerCase() === normalizedSearch ||
      label.toLowerCase() === normalizedSearch,
  );
  const showCreate = creatable && trimmed !== "" && !exactMatchExists;

  const commandPanel = (
    <Command
      className={cn(
        "bg-white text-gray-900",
        isMobile ? "flex h-full min-h-0 rounded-none" : "rounded-md",
      )}
    >
      {searchable ? (
        <CommandInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search..."
          disabled={disabled}
          className="text-sm"
        />
      ) : null}
      <CommandList
        className={cn(
          "flex flex-col",
          isMobile
            ? "h-full max-h-none min-h-0 flex-1 p-2 pb-[env(safe-area-inset-bottom,1.5rem)]"
            : "max-h-64 p-1",
        )}
      >
        {children}
        {showCreate ? (
          <div className="order-last shrink-0">
            {/* biome-ignore lint/a11y/useSemanticElements: intentional div-button to avoid nested <button> inside trigger */}
            <div
              role="button"
              tabIndex={0}
              className={cn(
                "flex cursor-pointer items-center gap-2 text-gray-900 hover:bg-gray-100",
                isMobile
                  ? "px-4 py-3 text-base"
                  : "rounded-sm px-2 py-2 text-sm",
              )}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                handleItemSelect(trimmed, trimmed);
                setSearch("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleItemSelect(trimmed, trimmed);
                  setSearch("");
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              <Plus className="size-4 shrink-0 text-gray-500" />
              <span>
                Create "<strong>{trimmed}</strong>"
              </span>
            </div>
          </div>
        ) : null}
      </CommandList>
    </Command>
  );

  if (isMobile) {
    return (
      <>
        {!open ? <HiddenHydrationList>{children}</HiddenHydrationList> : null}
        <MobileSelectDrawer
          open={open}
          onOpenChange={handleOpenChange}
          label={sheetLabel}
          searchable={searchable}
          className={className}
        >
          {commandPanel}
        </MobileSelectDrawer>
      </>
    );
  }

  return (
    <>
      {!open ? <HiddenHydrationList>{children}</HiddenHydrationList> : null}
      <PopoverContent
        align={align}
        side={side}
        sideOffset={sideOffset}
        className={cn(
          "z-[500000] w-[var(--radix-popover-trigger-width)] max-w-[var(--radix-popover-trigger-width)] p-1",
          className,
        )}
        onCloseAutoFocus={(e) => e.preventDefault()}
        {...enhancedContentProps}
      >
        {commandPanel}
      </PopoverContent>
    </>
  );
}

SelectContent.displayName = SelectPrimitive.Content.displayName;
